/**
 * useFocusVisible — le focus d'un élément se montre-t-il ? La règle de `:focus-visible` du web.
 *
 * `<Pressable>` dit `focused` sur le web pour tout focus, celui d'un clic comme celui du clavier : un
 * bouton cliqué garde le focus, et l'anneau restait autour de lui (Wajih, 20/10, le partage d'écran et
 * la main levée). Le navigateur, lui, sait si ce focus doit se voir (`:focus-visible`) : au clavier,
 * oui ; au doigt, jamais ; à la souris, presque jamais (un champ de texte, oui). On lui pose la
 * question, sur l'élément qui a le focus, au moment où il le prend, puis à chaque touche pressée
 * tant qu'il le garde : la souris qui a cliqué puis la touche Entrée, et l'anneau paraît, comme au web.
 *
 * Un navigateur qui ne connaît pas `:focus-visible` montre le focus, comme avant. Hors du web, rien
 * ne change : `focused` n'y vient que d'un clavier.
 */
import { useLayoutEffect, useState } from "react"
import { Platform } from "react-native"

/** L'élément qui a le focus le montre-t-il ? `true` quand le navigateur ne sait pas répondre. */
export function activeFocusVisible(): boolean {
    if (typeof document === "undefined") return true
    const active = document.activeElement
    if (active == null || typeof active.matches !== "function") return true
    try {
        return active.matches(":focus-visible")
    } catch {
        // Un navigateur sans `:focus-visible` refuse le sélecteur : le focus se voit, comme avant.
        return true
    }
}

export function useFocusVisible(focused: boolean): boolean {
    const web = Platform.OS === "web"
    // État d'interface : le focus de l'élément, tel que le navigateur le montre.
    const [visible, setVisible] = useState(false)
    useLayoutEffect(() => {
        if (!web) return undefined
        if (!focused) {
            setVisible(false)
            return undefined
        }
        setVisible(activeFocusVisible())
        // Une touche pressée pendant que l'élément garde le focus : le clavier a pris la main.
        const onKey = () => setVisible(activeFocusVisible())
        document.addEventListener("keyup", onKey, true)
        return () => document.removeEventListener("keyup", onKey, true)
    }, [focused, web])
    return web ? focused && visible : focused
}
